import { BaseScreen } from '@goodboydigital/astro';
import { Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { ShopType } from '../game/systems/ShopSystem';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { RectangleButton } from '../ui/buttons/RectangleButton';
import { ShopConfirmButton } from '../ui/buttons/ShopConfirmButton';
import LocalisedText from '../ui/LocalisedText';
import { ShopIcon } from '../ui/ShopIcon';
import { ShopPanel } from '../ui/ShopPanel';

export interface RideStudioBaseShopScreenOptions
{
    panelWidth?: number;
    panelHeight?: number;
    panelPadding?: number;
}

export class RideStudioBaseShopScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public backButton!: RectangleButton;
    public confirmButton!: ShopConfirmButton;

    public leftPanel!: ShopPanel;
    public rightPanel!: ShopPanel;

    public iconHash: Record<string, ShopIcon>;
    public paintIconHash: Record<string, ShopIcon>;

    protected type: ShopType;
    protected options: RideStudioBaseShopScreenOptions;

    protected panelWidth: number;
    protected panelPadding = 10;
    protected panelHeight = 480;

    constructor(app: App, options: RideStudioBaseShopScreenOptions)
    {
        super(app);
        this.app = app;
        this.iconHash = {};
        this.paintIconHash = {};
        this.type = 'default';
        this.options = options;

        this.panelWidth = options.panelWidth || 300;
        this.panelPadding = options.panelWidth || 10;
        this.panelHeight = options.panelWidth || 480;
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.backButton = new RectangleButton({
            text: 'rideStudioScreen_cancel',
            bg: 'red',
            w: 220,
            h: 80,
            fontSize: 36,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() => { this.onCancelButtonPressed() });
        
        this.confirmButton = new ShopConfirmButton({
            text: 'rideStudioScreen_confirm',
            bg: 'green',
            w: 220,
            h: 80,
            fontSize: 36,
        });
        this.view.addChild(this.confirmButton.view);
        this.confirmButton.onPress.connect(() => { this.onConfirmButtonPressed() });

        this.title = new LocalisedText('rideStudioScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 62,
            maxWidth: 450,
        });
        this.title.anchor.set(0.5);
        this.view.addChild(this.title);

        this.buildLeftPanel();
        this.buildRightPanel();
    }

    public show(): void
    {
        this.resize(this.width, this.height);

        this.view.visible = true;
        
        // Shared.game.soundSystem.playMusic('menu-music');
    }

    public hidden(): void
    {
        this.view.visible = false;
    }

    public resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageCenter.x - 120,
            this.app.stageBottom - 90,
        );
        
        this.confirmButton.view.position.set(
            this.app.stageCenter.x + 120,
            this.app.stageBottom - 90,
        );

        this.title.x = this.app.stageCenter.x;
        this.title.y = this.app.stageTop + (this.title.height / 2) + 20;

        this.leftPanel.view.position.set(
            this.app.stageLeft + (this.panelWidth / 2) + this.panelPadding,
            this.app.stageCenter.y - 0,
        );

        this.rightPanel.view.position.set(
            this.app.stageRight - (this.panelWidth / 2) - this.panelPadding,
            this.app.stageCenter.y - 0,
        );
    }
    
    protected onCancelButtonPressed(): void
    {
        this.app.controller.toRideStudio();
    }
    
    protected onConfirmButtonPressed(): void
    {
        this.app.controller.toRideStudio();
    }

    protected buildLeftPanel(): void
    {
        this.leftPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
        });
        this.view.addChild(this.leftPanel.view);
    }

    protected buildRightPanel(): void
    {
        this.rightPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
        });
        this.view.addChild(this.rightPanel.view);
    }
}
